import React, { useState } from 'react';
import { YieldGridData } from '../../services/yieldService';

interface YieldHeatmapGridProps {
  data: YieldGridData;
  onCellClick?: (blockId: string, inverterId: string, yieldValue: number) => void;
}

export const YieldHeatmapGrid: React.FC<YieldHeatmapGridProps> = ({ 
  data, 
  onCellClick 
}) => {
  const [hoveredCell, setHoveredCell] = useState<{
    blockId: string;
    inverterId: string;
    yieldValue: number;
    x: number;
    y: number;
  } | null>(null);

  const handleCellHover = (
    blockId: string, 
    inverterId: string, 
    yieldValue: number, 
    event: React.MouseEvent
  ) => {
    setHoveredCell({
      blockId,
      inverterId,
      yieldValue,
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleCellLeave = () => {
    setHoveredCell(null);
  };

  const getCellValue = (blockId: string, inverterId: string) => {
    return data.grid_data[blockId]?.[inverterId];
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Inverter Specific Yield Comparison</h3>
        <p className="text-sm text-gray-600">
          {data.summary.total_records} data points | Avg: {data.summary.avg_yield} kWh/kWp
        </p>
      </div>

      <div className="overflow-auto max-h-96">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-gray-50 border p-2 text-xs font-semibold min-w-20">
                Block/Inv
              </th>
              {data.inverters.map((inverterId) => (
                <th 
                  key={inverterId}
                  className="border p-2 text-xs font-semibold min-w-16 bg-gray-50"
                >
                  {inverterId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.blocks.map((blockId) => (
              <tr key={blockId}>
                <td className="sticky left-0 bg-gray-50 border p-2 text-xs font-semibold">
                  {blockId}
                </td>
                {data.inverters.map((inverterId) => {
                  const cellData = getCellValue(blockId, inverterId);
                  return (
                    <td
                      key={`${blockId}-${inverterId}`}
                      className="border p-1 text-xs text-center cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ 
                        backgroundColor: cellData?.color || '#f3f4f6',
                        color: cellData ? '#000' : '#9ca3af'
                      }}
                      onMouseEnter={(e) => 
                        cellData && handleCellHover(blockId, inverterId, cellData.yield, e)
                      }
                      onMouseLeave={handleCellLeave}
                      onClick={() => 
                        cellData && onCellClick?.(blockId, inverterId, cellData.yield)
                      }
                    >
                      {cellData ? cellData.yield : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="fixed z-50 bg-black text-white text-xs rounded px-2 py-1 pointer-events-none"
          style={{
            left: hoveredCell.x + 10,
            top: hoveredCell.y - 30
          }}
        >
          {hoveredCell.blockId} - {hoveredCell.inverterId}: {hoveredCell.yieldValue} kWh/kWp
        </div>
      )}
    </div>
  );
};